import React, { useState, createContext, useContext, useEffect, useMemo, useCallback, type ReactNode } from 'react'
import ReactDOM from 'react-dom'
import cx from 'classnames'
import './style.sass'

type MessageContextType = {
	setSuccessMessage: (message: string) => void
	setErrorMessage: (message: string) => void
}

const MessageContext = createContext<MessageContextType>({
	setSuccessMessage: () => {},
	setErrorMessage: () => {},
})

const Message = ({ children, type, onDismiss }: { children: ReactNode; type: string; onDismiss: () => void }) => {
	const isError = type === 'error'
	const isSuccess = type === 'success'

	const cs = cx('Message', {
		'Message--error': isError,
		'Message--success': isSuccess,
	})

	return (
		<div className='AnimatedMessageContainer'>
			<div className={cs}>
				<span>{children}</span>
				<button type='button' className='Message__dismiss' onClick={onDismiss} aria-label='Dismiss'>
					✕
				</button>
			</div>
		</div>
	)
}

export const MessageProvider = ({ children }: { children: ReactNode }) => {
	const [message, setMessage] = useState({ value: '' })
	const [type, setType] = useState('success')
	const [visible, setVisible] = useState(false)

	useEffect(() => {
		if (message.value !== '') {
			setVisible(true)
		}
	}, [message])

	const dismiss = useCallback(() => setVisible(false), [])

	const setMessageInfo = (message: string, type: string) => {
		setMessage(() => ({ value: message }))
		setType(type)
	}

	const setSuccessMessage = useCallback((message: string) => setMessageInfo(message, 'success'), [])

	const setErrorMessage = useCallback((message: string) => setMessageInfo(message, 'error'), [])

	const value = useMemo(() => ({ setSuccessMessage, setErrorMessage }), [setErrorMessage, setSuccessMessage])

	return (
		<MessageContext.Provider value={value}>
			{children}
			{ReactDOM.createPortal(
				<>{visible && <Message type={type} onDismiss={dismiss}>{message.value}</Message>}</>,
				document.getElementById('message-root') as HTMLElement,
			)}
		</MessageContext.Provider>
	)
}

export const withMessage = <P extends Record<string, unknown>>(Component: React.ComponentType<P>) => {
	return (props: Omit<P, 'setErrorMessage' | 'setSuccessMessage'>) => {
		const message = useContext(MessageContext)

		return <Component {...(props as P)} setErrorMessage={message.setErrorMessage} setSuccessMessage={message.setSuccessMessage} />
	}
}
